import { Layout } from '@/components/Layout/Layout';
import { Star, ImageIcon, Trash2, Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import { MainHeading } from '@/components/common/MainHeading';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { IReview, IReviewListResponse, ReviewTargetType, ReviewStatus } from '@/interfaces/Review/review.interface';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import { StatusBadge } from '@/components/common/StatusBadge';
import { formatDateTime, GetFilterUrl, showToastNotification, STATUS_OPTIONS, TARGET_TYPE_OPTIONS, downloadExcel } from '@/utility/helper';
import { useFilter } from '@/hooks/useFilter';
import Pagination from '@/components/common/Pagination';
import { TableFilters } from '@/components/common/TableFilters';
import Button from "@/components/common/Button";
import DateRangePicker from "@/components/common/DateRange";
import { Range } from "react-date-range";
import { format } from "date-fns";
import { StarRating } from '@/components/common/StarRating';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { IFilter } from '@/interfaces/common.interface';
import { usePermissions } from '@/hooks/usePermissions';
import { RESOURCES } from '@/context/permissions';
import { useActiveRole } from '@/hooks/useActiveRole';
import { RoutesLink } from '@/utility/RouteLinks';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';

export const Reviews = () => {
    const [reviews, setReviews] = useState<IReview[]>([]);
    const [loading, setLoading] = useState(false);
    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [selectedReview, setSelectedReview] = useState<IReview | null>(null);

    const { filters, debouncedFilters, updateFilter, handlePageChange, pageInfo, setPageInfo } = useFilter<IFilter>({
        page: 1,
        limit: 10,
        search: '',
        status: '',
        targetType: '',
        startDate: '',
        endDate: '',
    });

    const [loadingExport, setLoadingExport] = useState<boolean>(false);

    const handleExport = async () => {
        const queryParams = GetFilterUrl({
            status: filters.status,
            startDate: filters.startDate,
            endDate: filters.endDate,
            sort: filters.sort,
            targetType: filters.targetType
        });
        const url = `${API_URL + API_ENDPOINT.review}/export/excel?${queryParams}`;
        setLoadingExport(true);
        try {
            await downloadExcel(url, 'reviews_export.xlsx');
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoadingExport(false);
        }
    };

    const [dateRange, setDateRange] = useState<Range[]>([
        {
            startDate: undefined,
            endDate: undefined,
            key: 'selection',
        },
    ]);

    const handleDateChange = (newRange: any) => {
        const resolvedRange = typeof newRange === 'function' ? newRange(dateRange) : newRange;
        setDateRange(resolvedRange);

        const start = resolvedRange[0]?.startDate;
        const end = resolvedRange[0]?.endDate;
        const formattedStart = start ? format(start, 'yyyy-MM-dd') : '';
        const formattedEnd = end ? format(end, 'yyyy-MM-dd') : '';

        if (formattedStart !== filters.startDate || formattedEnd !== filters.endDate) {
            updateFilter({ startDate: formattedStart, endDate: formattedEnd, page: 1 });
        }
    };

    const { canDelete, isAdmin, canView, canExport } = usePermissions();
    const navigate = useRoleNavigate();
    const fetchReviews = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);
            const response = await callApi<IReviewListResponse>(
                `${API_URL}${API_ENDPOINT.review}?${queryParams.toString()}`,
                { method: 'GET' }
            );

            setReviews(response.data?.reviews || []);
            setPageInfo(
                response.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 }
            );
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews();
    }, [
        debouncedFilters.page,
        debouncedFilters.limit,
        debouncedFilters.search,
        debouncedFilters.status,
        debouncedFilters.targetType,
        debouncedFilters.startDate,
        debouncedFilters.endDate
    ]);

    const handleDeleteClick = (review: IReview) => {
        setSelectedReview(review);
        setIsModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedReview) return;

        setActionLoading(true);
        try {
            await callApi(
                `${API_URL}${API_ENDPOINT.review}/${selectedReview.id}`,
                {
                    method: 'DELETE',
                }
            );

            showToastNotification('success', `Review deleted successfully`);
            setIsModalOpen(false);
            fetchReviews();
        } catch (error) {
            ThrowError(error);
        } finally {
            setActionLoading(false);
        }
    };

    const columns: TableColumn<IReview>[] = [
        {
            key: 'order',
            header: 'Order ID',
            render: (review) => (
                <div
                    className="font-semibold text-primary cursor-pointer hover:underline"
                    onClick={() => navigate(RoutesLink.ViewOrders.replace(':id', review.orderId))}
                >
                    {review.order?.orderId || review.orderId || 'N/A'}
                </div>
            )
        },
        {
            key: 'user',
            header: 'User',
            render: (review) => (
                <div
                    className={`flex flex-col ${canView(RESOURCES.CUSTOMER) ? 'cursor-pointer group' : ''}`}
                    onClick={() => review.userId && canView(RESOURCES.CUSTOMER) && navigate(RoutesLink.CustomerDetails.replace(':id', review.userId))}
                >
                    <span className={`font-medium ${canView(RESOURCES.CUSTOMER) ? 'group-hover:text-primary group-hover:underline transition-colors' : ''}`}>{review.user?.fullName || 'Unknown User'}</span>
                    <span className="text-xs text-muted-foreground">{review.user?.phone || review.userId}</span>
                </div>
            )
        },
        {
            key: 'target',
            header: 'Target',
            render: (review) => {
                const isDriver = review.targetType === "on_driver";
                const targetName = isDriver ? review.target?.fullName || "Anonymous" : review.target?.name || 'Unknown Target';
                const handleTargetClick = () => {
                    if (!review.targetId) return;
                    if (isDriver) {
                        if (canView(RESOURCES.DRIVER)) {
                            navigate(RoutesLink.DriverDetails.replace(':id', review.targetId));
                        }
                    } else {
                        if (canView(RESOURCES.PRODUCT)) {
                            navigate(RoutesLink.ViewProduct.replace(':id', review.targetId));
                        }
                    }
                };
                return (
                    <div className={`flex items-center gap-3 ${canView(isDriver ? RESOURCES.DRIVER : RESOURCES.PRODUCT) ? 'cursor-pointer group' : ''}`} onClick={handleTargetClick}>
                        {review.target?.media?.[0] ? (
                            <img
                                src={`${API_URL}/${review.target.media[0].url}`}
                                alt={targetName}
                                className="w-8 h-8 rounded object-cover border border-secondary/20"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'https://placehold.co/100x100?text=No+Image';
                                }}
                            />
                        ) : (
                            <div className="w-8 h-8 rounded bg-secondary/20 flex items-center justify-center border border-secondary/20">
                                <ImageIcon className="w-4 h-4 text-muted-foreground/50" />
                            </div>
                        )}
                        <div className="flex flex-col">
                            <span className={`font-medium ${canView(isDriver ? RESOURCES.DRIVER : RESOURCES.PRODUCT) ? 'group-hover:text-primary group-hover:underline transition-colors' : ''}`}>{targetName}</span>
                            <span className="text-[10px] text-muted-foreground capitalize">{review.targetType?.replace('on_', '') || ''}</span>
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'rating',
            header: 'Rating',
            render: (review) => <StarRating rating={review.rating} />,
        },
        {
            key: 'targetType',
            header: 'Type',
            render: (review) => <StatusBadge status={review.targetType} />,
        },
        {
            key: 'comment',
            header: 'Comment',
            render: (review) => (
                <p className="text-sm text-muted-foreground max-w-xs truncate" title={review.comment}>
                    {review.comment || <span className="italic opacity-50">No comment</span>}
                </p>
            ),
        },
        // {
        //     key: 'status',
        //     header: 'Status',
        //     render: (review) => <StatusBadge status={review.status} />,
        // },
        {
            key: 'createdAt',
            header: 'Date',
            render: (review) => (
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDateTime(review.createdAt)}
                </span>
            ),
        },
    ];

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title="Reviews & Ratings"
                    description="Monitor user feedback across products, stores, drivers, and orders."
                />

                <TableFilters
                    searchTerm={filters.search}
                    onSearchChange={(val) => updateFilter({ search: val })}
                    placeholder="Search in comments..."
                >
                    <div className="flex items-center gap-3 flex-wrap">
                        <select
                            className="h-10 px-3 rounded-xl border border-secondary/20 bg-card text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                            value={filters.targetType}
                            onChange={(e) => updateFilter({ targetType: e.target.value as ReviewTargetType | '' })}
                        >
                            {TARGET_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        <DateRangePicker
                            dateRange={dateRange}
                            setDateRange={handleDateChange as any}
                        />
                        {canExport(RESOURCES.REVIEW) && <Button
                            variant="outline"
                            onClick={() => handleExport()}
                            className="h-10 px-4 flex items-center gap-2 rounded-xl"
                            leftIcon={<Download className="w-4 h-4" />}
                            isLoading={loadingExport}
                        >
                            Export
                        </Button>}
                    </div>
                </TableFilters>

                <CommonTable
                    columns={columns}
                    data={reviews}
                    loading={loading}
                    emptyMessage="No reviews found."
                    actions={(review) => {
                        const availableActions = [];
                        if (canDelete(RESOURCES.REVIEW)) {
                            availableActions.push({
                                text: 'Delete',
                                icon: <Trash2 className="w-4 h-4 text-destructive cursor-pointer" />,
                                onClick: () => handleDeleteClick(review),
                            });
                        }
                        return availableActions;
                    }}
                />

                {pageInfo.totalPages > 1 && (
                    <Pagination
                        currentPage={pageInfo.page}
                        totalPages={pageInfo.totalPages}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>

            <ConfirmationModal
                open={isModalOpen}
                onOpenChange={setIsModalOpen}
                title="Delete Review?"
                message="Are you sure you want to delete this review? This action cannot be undone."
                onConfirm={handleConfirmDelete}
                loading={actionLoading}
                type="danger"
                confirmText="Delete"
                size='lg'
            />
        </Layout>
    );
};

export default Reviews;
